import { Button } from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import * as React from 'react';
import styled from 'styled-components';
import Constants from '../../../constants';
import ContentConstants from '../../../contentConstants';
import { AudienceSourceType, IAudience, ISurveyAdminSummary, SurveyStatusType } from '../../../models';
import { AccessCodeType } from '../../../models/AccessCodeType';
import { ProgressPanelStatus } from '../../../models/ProgressPanelStatus';
import { AudienceColumns, defaultAudienceColumns } from '../../../store/AudienceListStore';
import { SurveyHelper } from '../../../utilities';
import { ProgressPanelHelper } from '../../../utilities/hooks/useProgressPanelState';
import { AddAudienceButton } from '../../audiences/AddAudienceButton';
import { AudienceCopyModal } from '../../audiences/AudienceCopyModal';
import { AudienceMode } from '../../audiences/AudienceMode';
import { AudienceTable } from '../../audiences/table/AudienceTable';
import { CardLayout, Col10Col2, HeaderButtonContainer, Section, WarningModal } from '../../common';
import { SectionHeaderMedium } from '../../common/styles/globalStyles';
import { AudienceDuplicatesPullout } from './audience/AudienceDuplicatesPullout';
import { AudienceParticipantsSection } from './audience/AudienceParticipantsSection';
import { MenuSummary } from './menu/MenuSummary';
import { SurveyAdminMenu } from './menu/SurveyAdminMenu';

const AddAudienceButtonContainer = styled.div`
	position: relative;
	margin-bottom: 30px;
	text-align: right;
`;
AddAudienceButtonContainer.displayName = 'AddAudienceButtonContainer';
const AudienceButtonContainer = styled.div`
	clear: left;
`;
AudienceButtonContainer.displayName = 'AudienceButtonContainer';

export interface IAdminMenuProps {
	audiences: IAudience[];
	isLoadingAudiences: boolean;
	isSurveyPreviewLoading: boolean;
	isSurveyStatusCancelling: boolean;
	surveyAdminSummary?: ISurveyAdminSummary;
	surveyAdminSummaryLoading: boolean;
	surveyId: number;
	CancelSurvey: (surveyId: number) => void;
	Navigate: (route: string, surveyId: number, subRoute?: string) => void;
}

export class AdminMenu extends React.PureComponent<
	IAdminMenuProps,
	{
		openWarningModal: boolean;
	}
> {
	constructor(props: IAdminMenuProps) {
		super(props);

		this.state = {
			openWarningModal: false,
		};

		this.handleWarningModalClose = this.handleWarningModalClose.bind(this);
		this.handleWarningModalSubmit = this.handleWarningModalSubmit.bind(this);
		this.handleCancelSurveyClick = this.handleCancelSurveyClick.bind(this);
		this.getWarningCancelSurveyBody = this.getWarningCancelSurveyBody.bind(this);
		this.hasAudiences = this.hasAudiences.bind(this);
	}

	public render() {
		const {
			audiences,
			isLoadingAudiences,
			isSurveyPreviewLoading,
			isSurveyStatusCancelling,
			surveyAdminSummary,
			surveyAdminSummaryLoading,
			surveyId,
			Navigate: navigate,
		} = this.props;
		const { openWarningModal } = this.state;

		const openOn = SurveyHelper.getSurveyOpenDate(audiences);
		const closeOn = SurveyHelper.getSurveyCloseDate(audiences);
		const hasBeenPublished = surveyAdminSummary ? surveyAdminSummary.hasBeenPublished : false;
		const surveyStatus = ProgressPanelHelper.determineStatus(
			undefined,
			openOn,
			closeOn,
			surveyAdminSummary?.surveyStatus,
			audiences,
			hasBeenPublished,
		);
		const isCustomAccessCodeHRIS = audiences.some(
			(a) => a.audienceSourceType === AudienceSourceType.Hris && a.accessCodeType === AccessCodeType.Custom,
		);
		const shouldShowPendingParticipants =
			isCustomAccessCodeHRIS &&
			(surveyAdminSummary?.surveyStatus === SurveyStatusType.SchedulingInProgress ||
				surveyAdminSummary?.surveyStatus === SurveyStatusType.Scheduled ||
				surveyAdminSummary?.surveyStatus === SurveyStatusType.SurveyInProgress);

		const audienceColumns = _.filter([...defaultAudienceColumns], (c) => c.name !== AudienceColumns.actions);
		if (surveyAdminSummary?.audienceSourceType !== AudienceSourceType.Hierarchy) {
			audienceColumns.push({ name: AudienceColumns.copy, displayText: 'Copy', disabled: true, displayOrder: 6, checked: true });
		}

		return (
			<>
				<CardLayout
					loading={surveyAdminSummaryLoading || isLoadingAudiences || isSurveyPreviewLoading || isSurveyStatusCancelling}
					title={ContentConstants.SURVEY_ADMIN_HEADER_CONTROL_PANEL}
					header={
						<HeaderButtonContainer>
							<Button
								id='edit-survey-btn'
								color='primary'
								size='md'
								disabled={
									surveyAdminSummaryLoading ||
									isLoadingAudiences ||
									isSurveyPreviewLoading ||
									isSurveyStatusCancelling ||
									surveyAdminSummary === undefined ||
									!surveyAdminSummary.hasBeenPublished ||
									surveyAdminSummary.surveyStatus === SurveyStatusType.Cancelled ||
									surveyAdminSummary.surveyStatus === SurveyStatusType.SurveyCloseFailed
								}
								onClick={() => navigate(Constants.ROUTE_SURVEY_ADMIN, surveyId, Constants.SUBROUTE_SURVEY_ADMIN_EDIT_SURVEY)}
							>
								{ContentConstants.SURVEY_ADMIN_ACTION_EDIT_SURVEY}
							</Button>
						</HeaderButtonContainer>
					}
				>
					{surveyAdminSummary && <MenuSummary />}
					<Section id={'control-panel-actions-section'} sectionHeader={ContentConstants.SURVEY_ADMIN_CONTROL_PANEL_ACTIONS}>
						<SurveyAdminMenu surveyId={surveyId} handleCancelSurveyClick={this.handleCancelSurveyClick} />
					</Section>
					{this.hasAudiences() && (
						<Section id={'audience-list-section'} sectionHeader={ContentConstants.EVENTS}>
							{shouldShowPendingParticipants && <AudienceParticipantsSection surveyId={surveyId} audiences={this.props.audiences} />}
							<AudienceDuplicatesPullout />
							{surveyAdminSummary?.audienceSourceType !== AudienceSourceType.Hierarchy && (
								<Col10Col2
									leftContent={<SectionHeaderMedium>Audiences</SectionHeaderMedium>}
									rightContent={
										<AddAudienceButtonContainer>
											<AddAudienceButton
												surveyId={surveyId}
												mode={AudienceMode.Admin}
												disabled={surveyStatus === ProgressPanelStatus.Closed || surveyStatus === ProgressPanelStatus.Cancelled}
												dropdownPositionX={-186}
												dropdownPositionY={40}
											/>
										</AddAudienceButtonContainer>
									}
								/>
							)}
							<AudienceTable
								surveyId={surveyId}
								audiences={audiences}
								audienceDeleteInProgress={false}
								audienceSourceType={surveyAdminSummary?.audienceSourceType}
								mode={AudienceMode.Admin}
								columns={audienceColumns}
							/>
						</Section>
					)}
				</CardLayout>
				<AudienceCopyModal mode={AudienceMode.Admin} />
				<WarningModal
					isOpen={openWarningModal}
					onClose={this.handleWarningModalClose}
					onSubmit={this.handleWarningModalSubmit}
					contentHeadline={ContentConstants.WARNING_CANCEL_SURVEY_HEADLINE}
					contentBody={this.getWarningCancelSurveyBody()}
				/>
			</>
		);
	}

	public handleWarningModalSubmit() {
		this.setState({ openWarningModal: false });
		this.props.CancelSurvey(this.props.surveyId);
	}

	public handleWarningModalClose() {
		this.setState({ openWarningModal: false });
	}

	public handleCancelSurveyClick() {
		this.setState({ openWarningModal: true });
	}

	public getWarningCancelSurveyBody() {
		const { surveyAdminSummary } = this.props;
		let surveyBody = ContentConstants.WARNING_CANCEL_SURVEY_BODY;
		if (surveyAdminSummary) {
			surveyAdminSummary.summaryItems.forEach((summaryItem) => {
				if (summaryItem.propertyName === 'SurveyName') {
					surveyBody = `${surveyBody} ${summaryItem.value.toString()} ?`;
				}
			});
		}
		return surveyBody;
	}

	private hasAudiences() {
		const { audiences } = this.props;

		if (audiences.length === 0 || (audiences.length === 1 && _.some(audiences, (a) => a.audienceId === 0))) {
			return false;
		}

		return true;
	}
}
